import { useMemo } from 'react';
import { useAuthQuery } from './useAuthQuery';
import { useAuth } from './useAuth';
import { useAdminCheck } from './useAdminCheck';

export interface ProjectTableIconPermissions {
  // Opty Column
  stakeholder: boolean;
  
  // GSheet Column
  procurement: boolean;
  documentation: boolean;
  shopDrawing: boolean;
  cost: boolean;
  
  // GDrive Column
  projectFolder: boolean;
  buildFolder: boolean;
  docFolder: boolean;
  reqFolder: boolean;
  proposalFolder: boolean;
  techFolder: boolean;
  reportFolder: boolean;
  specsFolder: boolean;
  poFolder: boolean;
  shopFolder: boolean;
  
  // Tools Column
  editProject: boolean;
  managePermissions: boolean;
  
  // Import Column
  importProject: boolean;
  
  // NEW GDrive Folders (consolidated)
  newGdriveFolders: boolean;
  
  // SUM Column (Admin only)
  toggleSumSetting: boolean;
}

const ICON_PERMISSION_CODES = [
  'ICON_STAKEHOLDER_VIEW',
  'ICON_PROCUREMENT_VIEW',
  'ICON_DOCUMENTATION_VIEW', 
  'ICON_SHOP_DRAWING_VIEW',
  'ICON_COST_VIEW',
  'ICON_PROJECT_FOLDER_VIEW',
  'ICON_BUILD_FOLDER_VIEW',
  'ICON_DOC_FOLDER_VIEW',
  'ICON_REQ_FOLDER_VIEW',
  'ICON_PROPOSAL_FOLDER_VIEW',
  'ICON_TECH_FOLDER_VIEW',
  'ICON_REPORT_FOLDER_VIEW',
  'ICON_SPECS_FOLDER_VIEW',
  'ICON_PO_FOLDER_VIEW',
  'ICON_SHOP_FOLDER_VIEW',
  'ICON_EDIT_PROJECT',
  'ICON_MANAGE_PERMISSIONS',
  'ICON_IMPORT_PROJECT',
  'ICON_NEW_GDRIVE_FOLDERS_VIEW'
] as const;

export const useProjectTableIconPermissions = (): {
  permissions: ProjectTableIconPermissions;
  isLoading: boolean;
} => {
  const { user } = useAuth();
  const { isAdmin, isLoading: isAdminLoading } = useAdminCheck();
  
  // Admin permissions - all true
  const adminPermissions = useMemo((): ProjectTableIconPermissions => ({
    stakeholder: true,
    procurement: true,
    documentation: true,
    shopDrawing: true,
    cost: true,
    projectFolder: true,
    buildFolder: true,
    docFolder: true,
    reqFolder: true,
    proposalFolder: true,
    techFolder: true,
    reportFolder: true,
    specsFolder: true,
    poFolder: true,
    shopFolder: true,
    editProject: true,
    managePermissions: true,
    importProject: true,
    newGdriveFolders: true,
    toggleSumSetting: true, // Admin only
  }), []);
  
  const permissionQueries = useAuthQuery<Record<string, boolean>>({
    queryKey: ['project-table-icon-permissions', user?.id],
    queryFn: async () => {
      const { checkUserPermission } = await import('@/lib/queries/authQueries');
      
      if (!user?.id) {
        return {};
      }

      // Check all icon permissions in parallel
      const permissionChecks = await Promise.all(
        ICON_PERMISSION_CODES.map(async (code) => {
          const hasPermission = await checkUserPermission(user.id, code);
          return [code, hasPermission] as const;
        })
      );

      return Object.fromEntries(permissionChecks);
    },
    requiresAuth: true,
    requiresSession: true,
    enabled: !!user?.id && !isAdmin,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // ✅ Move useMemo BEFORE early return to comply with Rules of Hooks
  const normalPermissions = useMemo((): ProjectTableIconPermissions => {
    const data = permissionQueries.data || {};
    
    return {
      // Opty Column
      stakeholder: data['ICON_STAKEHOLDER_VIEW'] || false,
      
      // GSheet Column
      procurement: data['ICON_PROCUREMENT_VIEW'] || false,
      documentation: data['ICON_DOCUMENTATION_VIEW'] || false,
      shopDrawing: data['ICON_SHOP_DRAWING_VIEW'] || false,
      cost: data['ICON_COST_VIEW'] || false,
      
      // GDrive Column
      projectFolder: data['ICON_PROJECT_FOLDER_VIEW'] || false,
      buildFolder: data['ICON_BUILD_FOLDER_VIEW'] || false,
      docFolder: data['ICON_DOC_FOLDER_VIEW'] || false,
      reqFolder: data['ICON_REQ_FOLDER_VIEW'] || false,
      proposalFolder: data['ICON_PROPOSAL_FOLDER_VIEW'] || false,
      techFolder: data['ICON_TECH_FOLDER_VIEW'] || false,
      reportFolder: data['ICON_REPORT_FOLDER_VIEW'] || false,
      specsFolder: data['ICON_SPECS_FOLDER_VIEW'] || false,
      poFolder: data['ICON_PO_FOLDER_VIEW'] || false,
      shopFolder: data['ICON_SHOP_FOLDER_VIEW'] || false,
      
      // Tools Column
      editProject: data['ICON_EDIT_PROJECT'] || false,
      managePermissions: data['ICON_MANAGE_PERMISSIONS'] || false,
      
      // Import Column
      importProject: data['ICON_IMPORT_PROJECT'] || false,
      
      // NEW GDrive Folders
      newGdriveFolders: data['ICON_NEW_GDRIVE_FOLDERS_VIEW'] || false,
      
      // SUM Column - non-admin always false
      toggleSumSetting: false,
    };
  }, [permissionQueries.data]);

  // ✅ Admin bypass - return after all hooks are called
  if (isAdmin && !isAdminLoading) {
    return {
      permissions: adminPermissions,
      isLoading: false,
    };
  }

  return {
    permissions: normalPermissions,
    isLoading: permissionQueries.isLoading,
  };
};